import api from './api'
import type {
  ClubApplicationDetail,
  ClubApplicationsResponse,
  ClubSignupPayload,
  ClubSignupResponse,
  MyClubApplication,
  PublicSignupResponse,
  SignupLink
} from '../types/clubApplication.types'

/**
 * Club Application Service
 * Handles club signup ("Zapisy do klubu") API calls
 */
class ClubApplicationService {
  /**
   * Get public club info for signup page
   * GET v1/public/signup/{token}
   * With an invite token (link from an invitation e-mail) the backend records the click
   * and returns the invited address.
   */
  async getPublicClub(token: string, invite?: string | null): Promise<PublicSignupResponse> {
    const params = invite ? { invite } : undefined
    const response = await api.get<PublicSignupResponse>(`v1/public/signup/${token}`, { params })
    return response.data
  }

  /**
   * Submit signup form
   * POST v1/public/signup/{token}
   */
  async submitSignup(token: string, data: ClubSignupPayload): Promise<ClubSignupResponse> {
    const response = await api.post<ClubSignupResponse>(`v1/public/signup/${token}`, data)
    return response.data
  }

  /**
   * Get pending applications
   * GET v1/{club}/club-applications
   */
  async getApplications(
    clubUuid: string,
    page: number = 1,
    perPage: number = 25,
    search: string = ''
  ): Promise<ClubApplicationsResponse> {
    const params: Record<string, any> = { page, per_page: perPage }
    if (search.trim()) params.search = search.trim()
    const response = await api.get<ClubApplicationsResponse>(`v1/${clubUuid}/club-applications`, { params })
    return response.data
  }

  /**
   * Get pending applications count
   * GET v1/{club}/club-applications/count
   */
  async getCount(clubUuid: string): Promise<number> {
    const response = await api.get<{ count: number }>(`v1/${clubUuid}/club-applications/count`)
    return response.data.count
  }

  /**
   * Get single application
   * GET v1/{club}/club-applications/{uuid}
   */
  async getApplication(clubUuid: string, applicationUuid: string): Promise<ClubApplicationDetail> {
    const response = await api.get<{ application: ClubApplicationDetail }>(
      `v1/${clubUuid}/club-applications/${applicationUuid}`
    )
    return response.data.application
  }

  /**
   * Accept applications
   * POST v1/{club}/club-applications/accept
   */
  async accept(clubUuid: string, applicationUuids: string[], categoryUuids: string[] = []): Promise<{ message: string; accepted: number }> {
    const response = await api.post(`v1/${clubUuid}/club-applications/accept`, {
      application_uuids: applicationUuids,
      category_uuids: categoryUuids
    })
    return response.data
  }

  /**
   * Reject applications
   * POST v1/{club}/club-applications/reject
   */
  async reject(clubUuid: string, applicationUuids: string[]): Promise<{ message: string; rejected: number }> {
    const response = await api.post(`v1/${clubUuid}/club-applications/reject`, {
      application_uuids: applicationUuids
    })
    return response.data
  }

  /**
   * Get signup link
   * GET v1/{club}/club-applications/signup-link
   */
  async getSignupLink(clubUuid: string): Promise<SignupLink> {
    const response = await api.get<SignupLink>(`v1/${clubUuid}/club-applications/signup-link`)
    return response.data
  }

  /**
   * Enable / disable signups
   * PUT v1/{club}/club-applications/signup-link
   */
  async updateSignupLink(clubUuid: string, enabled: boolean): Promise<SignupLink> {
    const response = await api.put<SignupLink>(`v1/${clubUuid}/club-applications/signup-link`, { enabled })
    return response.data
  }

  /**
   * Regenerate signup link (old one stops working)
   * POST v1/{club}/club-applications/signup-link/regenerate
   */
  async regenerateSignupLink(clubUuid: string): Promise<SignupLink> {
    const response = await api.post<SignupLink>(`v1/${clubUuid}/club-applications/signup-link/regenerate`)
    return response.data
  }

  /**
   * Download QR code PDF
   * GET v1/{club}/club-applications/signup-link/qr-pdf
   */
  async downloadQrPdf(clubUuid: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await api.get(`v1/${clubUuid}/club-applications/signup-link/qr-pdf`, {
      responseType: 'blob'
    })
    let fileName = 'zapisy-qr.pdf'
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
      if (match) fileName = match[1]
    }
    return { blob: new Blob([response.data], { type: 'application/pdf' }), fileName }
  }

  /**
   * Get current user's own applications (for choose-profile screen)
   * GET v1/auth/club-applications
   */
  async getMyApplications(): Promise<MyClubApplication[]> {
    const response = await api.get<{ applications: MyClubApplication[] }>('v1/auth/club-applications')
    return response.data.applications || []
  }
}

export default new ClubApplicationService()
